import { InvestorDetailsPacingTabCommitmentPlanQuery } from '@/__generated__/graphql';
import { EditIcon } from '@/assets/icons/edit';
import { ExportDataIcon } from '@/assets/icons/exportDataIcon';
import Button from '@/components/basicComponents/button';
import Select, { ISelectValue } from '@/components/basicComponents/select';
import FormattingTooltip from '@/components/basicComponents/tooltip/dollarsTooltip';
import { useResponsive } from '@/hooks/use-responsive';
import { useSearchParamsManager } from '@/hooks/use-search-params';
import { getUTCDate } from '@/shared/utils/date';
import { themeVar } from '@/styles/theme';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import DrawdownTarget from '../modals/drawdownTarget';
import { AssetClassForecastSummaryDto, DrawdownTargetsDto } from '../types';
import AssetClassTable from './assetClassTable';
import ZeroStateAssetClassTable from './zeroStateAssetClassTable';

type MakeOptional<T> = {
  [K in keyof T]?: T[K];
};

interface IPacingTabDetailsData
  extends MakeOptional<{
    id: string | null;
    name: string;
    startDate: any;
    startingPortfolioValue: number;
    drawdownTarget: number;
    startingAllocation: number;
    growthRate: number;
    drawdownTargets: InvestorDetailsPacingTabCommitmentPlanQuery['commitmentPlan']['drawdownTargets'];
    assetClassDrawdownTargets: InvestorDetailsPacingTabCommitmentPlanQuery['commitmentPlan']['assetClassDrawdownTargets'];
    forecastSummary: InvestorDetailsPacingTabCommitmentPlanQuery['commitmentPlan']['forecastSummary'];
  }> {}

interface PacingDetailsProps {
  mode: 'zeroState' | 'editCommitmentPlan' | 'pacingTab' | 'waiting';
  detailsData: IPacingTabDetailsData | null;
  assetClassForecasts?: AssetClassForecastSummaryDto[];
  entityName?: string;
  selectedCommitmentPlanId?: string;
  commitmentPlanSelectOptions?: ISelectValue[];
  drawdownTargetDialogData?: DrawdownTargetsDto | null;
  onChangeCommitmentPlan?: (id: string) => void;
  onEditButtonClick?: () => void;
}

export const PacingDetails = ({
  mode,
  detailsData,
  entityName,
  assetClassForecasts,
  selectedCommitmentPlanId,
  commitmentPlanSelectOptions,
  drawdownTargetDialogData,
  onChangeCommitmentPlan,
  onEditButtonClick
}: PacingDetailsProps) => {
  const { isMobile } = useResponsive();
  const navigate = useNavigate();
  const params = useParams();
  const { searchParams } = useSearchParamsManager();

  const investorId = params?.investorId || '';
  const entityId = params?.entityId || '';

  const isGroup = searchParams.get('isGroup') === 'true';

  const [modalWindow, setModalWindow] = useState({ isOpen: false, type: 'drawdown-target' });

  const openModalWindow = (type: string) => {
    setModalWindow({ type, isOpen: true });
  };

  const closeModalWindow = () => {
    setModalWindow({ ...modalWindow, isOpen: false });
  };

  return (
    <>
      {modalWindow.isOpen && modalWindow.type === 'drawdown-target' && (
        <DrawdownTarget isOpen={modalWindow.isOpen} onClose={closeModalWindow} drawdownTargetDialogData={drawdownTargetDialogData} />
      )}
      <PricingDetailsWrapper>
        <TopSectionWrapper>
          {mode !== 'zeroState' && (
            <SelectedPlan>
              <Text>Selected Plan</Text>
              <SelectWrapper>
                {mode === 'editCommitmentPlan' && <>{detailsData?.name}</>}
                {(mode === 'pacingTab' || mode === 'waiting') && (
                  <Select
                    value={selectedCommitmentPlanId}
                    options={commitmentPlanSelectOptions}
                    setValue={onChangeCommitmentPlan}
                    size="sm"
                    withSearch
                    additionalOption={
                      <AdditionalOptionWrapper
                        onClick={() => {
                          navigate(`allCommitmentPlans?entityName=${entityName}&isGroup=${isGroup}&backToTitle=Investor Details`);
                        }}
                      >
                        <EditIcon fill={themeVar('font.action')} />
                        <AddText>Edit Commitment Plans</AddText>
                      </AdditionalOptionWrapper>
                    }
                  />
                )}
              </SelectWrapper>
            </SelectedPlan>
          )}

          <TimescaleWrapper>
            {mode === 'pacingTab' && (
              <Button
                size="md"
                styleType="outline"
                onClick={() =>
                  navigate(
                    `/investors/${investorId}/${entityId}/editCommitmentPlan?commitmentPlanId=${
                      detailsData?.id ?? ''
                    }&isGroup=${isGroup}&backToTitle=Investor Details`
                  )
                }
              >
                Edit Commitment Plan
              </Button>
            )}
          </TimescaleWrapper>
        </TopSectionWrapper>

        <MoreDetails isMobile={isMobile}>
          <MoreDetailItem isMobile={isMobile}>
            {mode !== 'zeroState' && (
              <ItemRow>
                <ItemTitle>Plan Start</ItemTitle>
                <ItemValue>{detailsData?.startDate && getUTCDate(new Date(detailsData.startDate))}</ItemValue>
              </ItemRow>
            )}
            <ItemRow>
              <ItemTitle>Starting Portfolio Value</ItemTitle>
              <ItemValue>
                <FormattingTooltip zIndex={1000}>{detailsData?.startingPortfolioValue}</FormattingTooltip>
              </ItemValue>
            </ItemRow>
            <ItemRow>
              <ItemTitle>Starting Allocation</ItemTitle>
              <ItemValue>{`${typeof detailsData?.startingAllocation === 'number' ? detailsData.startingAllocation.toFixed(2) : 0}%`}</ItemValue>
            </ItemRow>
            {mode !== 'zeroState' && (
              <ItemRow>
                <ItemTitle>Growth Rate Ex. Drawdowns</ItemTitle>
                <ItemValue>{`${typeof detailsData?.growthRate === 'number' ? detailsData.growthRate.toFixed(2) : 0}%`}</ItemValue>
              </ItemRow>
            )}
            {mode === 'editCommitmentPlan' && (
              <CommitmentPlanButton onClick={onEditButtonClick}>
                <EditIcon fill={themeVar('action.primary')} />
                Edit Options
              </CommitmentPlanButton>
            )}
            {mode === 'pacingTab' && (
              <CommitmentPlanButton onClick={onEditButtonClick}>
                <ExportDataIcon fill={themeVar('action.primary')} />
                Export
              </CommitmentPlanButton>
            )}
          </MoreDetailItem>
          {(mode === 'pacingTab' || mode === 'editCommitmentPlan') && assetClassForecasts && (
            <AssetClassTableWrapper isMobile={isMobile}>
              <AssetClassTable
                assetClassForecasts={assetClassForecasts}
                fiveYearDate={detailsData?.forecastSummary?.fiveYearDate ?? null}
                tenYearDate={detailsData?.forecastSummary?.tenYearDate ?? null}
              />
            </AssetClassTableWrapper>
          )}
          {(mode === 'zeroState' || mode === 'waiting') && detailsData?.assetClassDrawdownTargets && (
            <AssetClassTableWrapper isMobile={isMobile}>
              <ZeroStateAssetClassTable assetClassTableData={detailsData?.assetClassDrawdownTargets ?? null} />
            </AssetClassTableWrapper>
          )}
        </MoreDetails>
      </PricingDetailsWrapper>
    </>
  );
};

const PricingDetailsWrapper = styled.div`
  padding: 10px 60px;
  background-color: ${themeVar('layer.1')};
  color: ${themeVar('font.base')};
`;

const TopSectionWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 10px;
`;

const Text = styled.p`
  color: ${themeVar('font.base')};
  font-size: 16px;
  font-weight: 700;
  line-height: 22.4px;
  padding: 10px;
`;

const SelectedPlan = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 50%;
`;

const SelectWrapper = styled.div`
  max-width: 535px;
  width: 100%;
`;

const TimescaleWrapper = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 20px;
`;

const AdditionalOptionWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
`;

const AddText = styled.div`
  font-weight: 600;
  font-size: 16px;
  line-height: 22px;
  color: ${themeVar('font.action')};
`;

const MoreDetails = styled.div<{ isMobile: boolean }>`
  display: flex;
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
  gap: ${({ isMobile }) => (isMobile ? '14px' : '30px')};
  justify-content: space-between;
  padding: 10px;
`;

const MoreDetailItem = styled.div<{ isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  width: ${({ isMobile }) => (isMobile ? '100%' : '40%')};
`;

const AssetClassTableWrapper = styled.div<{ isMobile: boolean }>`
  width: ${({ isMobile }) => (isMobile ? '100%' : '60%')};
`;

const ItemRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
  padding: 8px 10px;
`;

const ItemValue = styled.div<{ isRedirect?: boolean }>`
  font-family: Blinker, serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 22px;
  color: ${({ theme, isRedirect }) => (isRedirect ? themeVar('font.action') : themeVar('font.base'))};
  cursor: ${({ isRedirect }) => (isRedirect ? 'pointer' : 'default')};
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ItemTitle = styled.div`
  font-family: Blinker, serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
  color: ${themeVar('font.base')};
  white-space: nowrap;
  width: 160px;
`;

const CommitmentPlanButton = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 10px;
  color: ${themeVar('action.primary')};
  cursor: pointer;
  width: max-content;
`;
